import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';

import { Coins, Copy, CreditCard, FileText, Flag, Mail, MailCheck, UserCheck } from 'lucide-react';

import { useAppSelector } from '../../hooks';
import { getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { green01, greyP4, greyP5 } from '../../theme';

const SIDEBAR_WIDTH = 260;

export const AdminLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAppSelector(getUser);

  const adminPages = [
    { path: '/impersonate', label: t('navbar.impersonateUser'), icon: UserCheck },
    { path: '/invitation', label: t('navbar.sendInvitation'), icon: Mail },
    { path: '/credits', label: t('navbar.addUserCredits'), icon: Coins },
    { path: '/copy-project', label: t('navbar.copyProject'), icon: Copy },
    { path: '/feature-flags', label: t('navbar.featureFlags'), icon: Flag },
    { path: '/subscriptions', label: t('navbar.userSubscriptions'), icon: CreditCard },
    { path: '/email-subscriptions', label: t('navbar.emailSubscriptions'), icon: MailCheck },
    { path: '/audit-logs', label: t('navbar.auditLogs', 'Audit Logs'), icon: FileText },
  ];

  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: '100%' }}>
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          backgroundColor: greyP4,
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Typography
            variant="overline"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600,
              letterSpacing: '0.08em',
            }}
          >
            {t('admin.title', 'Admin')}
          </Typography>
        </Box>
        <List sx={{ flexGrow: 1, py: 1 }}>
          {adminPages.map(({ path, label, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <ListItemButton
                key={path}
                onClick={() => history.push(path)}
                sx={{
                  mx: 1,
                  my: 0.25,
                  borderRadius: 1.5,
                  color: active ? green01 : 'rgba(255, 255, 255, 0.85)',
                  backgroundColor: active ? 'rgba(179, 236, 73, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: active ? 'rgba(179, 236, 73, 0.12)' : greyP5,
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                  <Icon size={18} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: active ? 600 : 400,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 2, md: 4 } }}>
        <Outlet />
      </Box>
    </Box>
  );
};
